import React from "react";
import {
  FileText,
  FileSpreadsheet,
  Presentation,
  Archive,
  Download,
  File,
  FileImage,
  FileAudio,
  FileVideo,
  FileCode,
} from "lucide-react";
import { TextMessage, FileMetadata } from "../../../types/chat";
import styles from "./FileMessage.module.scss";

interface FileMessageProps {
  message: TextMessage;
  metadata: FileMetadata;
  isOwnMessage?: boolean;
}

const FileMessage: React.FC<FileMessageProps> = ({ message, metadata }) => {
  // Xác định icon và màu dựa vào mimeType và extension
  const getFileIcon = (mimeType: string, fileName: string) => {
    const type = mimeType.toLowerCase();
    const ext = fileName.split(".").pop()?.toLowerCase() || "";

    // PDF
    if (type.includes("pdf") || ext === "pdf") {
      return { icon: <FileText size={32} />, color: "#d32f2f" };
    }

    // Excel - Check BEFORE Word/document
    if (
      type.includes("excel") ||
      type.includes("spreadsheet") ||
      type.includes("sheet") ||
      ["xls", "xlsx", "csv"].includes(ext)
    ) {
      return { icon: <FileSpreadsheet size={32} />, color: "#4caf50" };
    }

    // Word
    if (type.includes("word") || type.includes("document") || ["doc", "docx"].includes(ext)) {
      return { icon: <FileText size={32} />, color: "#2196f3" };
    }

    // PowerPoint
    if (type.includes("powerpoint") || type.includes("presentation") || ["ppt", "pptx"].includes(ext)) {
      return { icon: <Presentation size={32} />, color: "#ff9800" };
    }

    // Archives
    if (
      type.includes("zip") ||
      type.includes("rar") ||
      type.includes("7z") ||
      ["zip", "rar", "7z", "tar", "gz"].includes(ext)
    ) {
      return { icon: <Archive size={32} />, color: "#9c27b0" };
    }

    // Images
    if (type.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"].includes(ext)) {
      return { icon: <FileImage size={32} />, color: "#e91e63" };
    }

    // Audio
    if (type.startsWith("audio/") || ["mp3", "wav", "ogg", "m4a", "flac"].includes(ext)) {
      return { icon: <FileAudio size={32} />, color: "#00bcd4" };
    }

    // Video
    if (type.startsWith("video/") || ["mp4", "avi", "mov", "wmv", "flv", "webm"].includes(ext)) {
      return { icon: <FileVideo size={32} />, color: "#ff5722" };
    }

    // Code files
    if (
      ["js", "ts", "jsx", "tsx", "py", "java", "cpp", "c", "cs", "php", "rb", "go", "rs", "swift"].includes(ext) ||
      type.includes("javascript") ||
      type.includes("typescript") ||
      type.includes("python")
    ) {
      return { icon: <FileCode size={32} />, color: "#795548" };
    }

    // Text files
    if (type.includes("text") || ["txt", "md", "log"].includes(ext)) {
      return { icon: <FileText size={32} />, color: "#607d8b" };
    }

    // Default for unknown types
    return { icon: <File size={32} />, color: "#757575" };
  };

  const { icon, color } = getFileIcon(metadata.mimeType || "", metadata.name || "");

  return (
    <div className={styles.fileMessage}>
      <a
        href={metadata.attachmentUrl}
        download={metadata.name}
        className={styles.fileLink}
        target="_blank"
        rel="noopener noreferrer"
      >
        <div className={styles.fileIcon} style={{ color }}>
          {icon}
        </div>
        <div className={styles.fileInfo}>
          <div className={styles.fileName}>{metadata.name}</div>
          <div className={styles.fileSize}>{metadata.sizeString}</div>
        </div>
        <Download className={styles.downloadIcon} size={20} />
      </a>
    </div>
  );
};

export default FileMessage;
